import { useCallback, useEffect, useRef } from 'react';

import { scrollRootOf } from './scrollRoot';

/** The row's measured height, px. Read by the row's transform and by the pin below it. */
export const ACTIVITY_H_VAR = '--activity-h';
/** How much of the row is showing right now, px — 0 (collapsed) to --activity-h (revealed). */
export const ACTIVITY_REVEAL_VAR = '--activity-reveal';

/**
 * The tournament day chips (owner, 2026-09-11): the second row to collapse this
 * way. Read by the chips' ground (its transform) and by the pinned column heads
 * beneath it, which ride up as the chips go.
 */
export const DAY_ROW_H_VAR = '--dayrow-h';
export const DAY_ROW_REVEAL_VAR = '--dayrow-reveal';

/** The pair of custom properties one collapsing row publishes on the scroll root. */
export interface CollapsingRowVars {
  /** The collapsible height, px. */
  h: string;
  /** How much of it is showing, px — 0 to `h`. */
  reveal: string;
}

export const ACTIVITY_ROW_VARS: CollapsingRowVars = {
  h: ACTIVITY_H_VAR,
  reveal: ACTIVITY_REVEAL_VAR
};
export const DAY_ROW_VARS: CollapsingRowVars = { h: DAY_ROW_H_VAR, reveal: DAY_ROW_REVEAL_VAR };

export interface CollapsingRowOptions {
  /** Which two properties this row writes. Defaults to the club-stats row's. */
  vars?: CollapsingRowVars;
  /**
   * The element whose height is the distance the row collapses by, when that is
   * not the whole pinned row. The day chips' row keeps a static gap above its
   * chips (solid ground under the panel's edge, so the chips slide UNDER the
   * panel rather than vanishing at a line below it); only the chips and the
   * gap beneath them collapse, so the extent is that inner ground, not the row.
   * Defaults to the row itself.
   */
  extent?: (row: HTMLElement) => HTMLElement | null;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// DEVICE PIXELS (owner, 2026-09-11). A row's measured height is fractional — a
// 91.5px chip row is typical — and every reveal derived from it inherited the
// half pixel, so the row and whatever is pinned beneath it sat BETWEEN device
// pixels for the whole collapse, and their text re-rasterised on every frame
// as they moved: the shimmer the owner read as jitter, on the day chips and,
// more faintly, on the club-stats row. The height snaps UP, so a collapsed row
// is never a sliver short of hidden; the reveal snaps to the nearest device
// pixel, then is clamped to the height so the two still meet exactly.
const dpr = () => Math.max(1, window.devicePixelRatio || 1);
const snapUp = (v: number) => Math.ceil(v * dpr() - 1e-6) / dpr();
const snap = (v: number) => Math.round(v * dpr()) / dpr();

/**
 * Direction-aware collapse for one pinned row (owner, 2026-09-08 — the club
 * stats under the compact header). The rows around it are fixed; this one
 * hides as the list scrolls DOWN and comes back FIRST as it scrolls UP — before
 * anything that scrolled away above the bar (the promo ribbon) — because the
 * first pixels of an upward scroll are spent re-revealing it, and the ribbon
 * only returns once the bar unpins at the top of the list. Every pixel of
 * scroll moves it by a pixel, so it tracks the finger; pausing does not reset
 * it.
 *
 * ONLY WHILE PINNED. In flow — the promo ribbon still (partly) in view, the bar
 * not yet stuck — the row stays fully open: the Your Tables panel below it is
 * in flow too and cannot follow a collapsing strip, so collapsing there opened
 * a widening gap between the two (measured on the first cut). Pinned is read
 * geometrically each scroll — the row's top has reached its sticky offset
 * inside the scroll root — so it needs no knowledge of what sits above the bar.
 *
 * HOW, and why not the obvious way: the row's box never changes size. It keeps
 * its full height in flow (so the list below it never reflows or jumps while
 * the gesture is live — the failure that retired the last scroll-driven header
 * in this lobby, see __tests__/SaloonLobby.headerScrollAway.test.tsx) and the
 * strip inside it translates up under the bar, clipped by the row. Two CSS
 * custom properties on the scroll root carry the state — the row's transform
 * and the Your Tables pin read them — so a scroll never touches React state
 * and never costs a render. Written from a passive scroll listener; measured
 * with a ResizeObserver so a font swap cannot leave a stale height.
 *
 * Returns a callback ref for the row. Attaching finds the scroll root from the
 * element (./scrollRoot) and starts fully revealed, which is where the row sits
 * in flow at the top of the list — so pinning it never jumps. Detaching (the
 * row unmounts when the header widens or the stats are withheld) zeroes both
 * variables so the pin chain closes up as if the row were never there.
 *
 * Two rows collapse this way now (owner, 2026-09-11): the club stats under the
 * compact header, and the tournament tab's day chips, which hide the same way
 * so the day-column heads beneath them can hold the top of the list instead.
 * Each names its own pair of properties (`options.vars`), so the two never
 * read each other's state.
 */
export function useCollapsingRow(
  options: CollapsingRowOptions = {}
): (el: HTMLDivElement | null) => void {
  const { vars = ACTIVITY_ROW_VARS, extent } = options;
  // Held in a ref so a caller passing a fresh object or arrow each render does
  // not re-create the callback ref (which would detach and re-attach the row).
  const optsRef = useRef({ vars, extent });
  optsRef.current = { vars, extent };
  const st = useRef<{
    root: HTMLElement | null;
    onScroll: (() => void) | null;
    ro: ResizeObserver | null;
    raf: number;
    h: number;
    reveal: number;
    lastTop: number;
    /** Where the row's top sat at the last scroll event, viewport px — see the arrival frame. */
    lastRowTop: number;
    /**
     * The row's sticky `top`, in px. Measured once on attach and refreshed by
     * the ResizeObserver, NOT re-read per scroll event (JRam, 2026-09-14): it
     * comes from the stylesheet and cannot change between two events of the
     * same gesture, while reading it forced a style recalculation every time —
     * the previous event's handler ends by writing a custom property on the
     * scroll root, so the read was always against invalidated style. EN-10744
     * adds a second instance of this hook, so on Tournaments under the compact
     * header two rows were each paying it on every event.
     */
    stickyTop: number;
    wasPinned: boolean;
  }>({
    root: null,
    onScroll: null,
    ro: null,
    raf: 0,
    h: 0,
    reveal: 0,
    lastTop: 0,
    lastRowTop: 0,
    wasPinned: false,
    stickyTop: 0
  });

  const detach = useCallback(() => {
    const s = st.current;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.raf = 0;
    if (s.root && s.onScroll) s.root.removeEventListener('scroll', s.onScroll);
    s.ro?.disconnect();
    if (s.root) {
      const { vars: v } = optsRef.current;
      s.root.style.setProperty(v.reveal, '0px');
      s.root.style.setProperty(v.h, '0px');
    }
    s.root = null;
    s.onScroll = null;
    s.ro = null;
  }, []);

  const setEl = useCallback(
    (el: HTMLDivElement | null) => {
      detach();
      if (!el) return;
      const s = st.current;
      const { vars: v, extent: pick } = optsRef.current;
      // What is measured: the row, unless the caller names an inner extent.
      const measured = pick?.(el) ?? el;

      const publish = (root: HTMLElement) => {
        root.style.setProperty(v.h, `${s.h}px`);
        root.style.setProperty(v.reveal, `${s.reveal}px`);
      };

      const attach = (attempt: number) => {
        // React attaches a parent's ref after its children's, so the scroll
        // root can be missing on the first call; retry a few frames, bounded.
        const root = scrollRootOf(el);
        if (!root) {
          if (attempt < 10) s.raf = requestAnimationFrame(() => attach(attempt + 1));
          return;
        }
        s.root = root;
        // getBoundingClientRect, not offsetHeight: the row's height is
        // fractional, and an integer here would open the same sub-pixel seam
        // the bar's measurement once did.
        s.h = snapUp(measured.getBoundingClientRect().height);
        s.reveal = s.h;
        s.lastTop = root.scrollTop;
        s.lastRowTop = el.getBoundingClientRect().top;
        s.wasPinned = false;
        s.stickyTop = parseFloat(getComputedStyle(el).top) || 0;
        publish(root);

        s.onScroll = () => {
          // Clamp to the REAL scroll range before measuring the delta. iOS
          // rubber-bands past both ends: at the bottom of a list scrollTop runs
          // beyond its maximum and then springs back, and that spring is a run
          // of NEGATIVE deltas — indistinguishable, to a plain subtraction,
          // from the finger scrolling up. The row would haul itself back into
          // view at the end of every list, in the spring's own stutter, with
          // nobody touching the screen (owner, on device, 2026-09-11; all three
          // tabs). Clamped, the overscroll region contributes a delta of zero,
          // so the row only ever moves for real scrolling.
          const max = Math.max(0, root.scrollHeight - root.clientHeight);
          const top = clamp(root.scrollTop, 0, max);
          const dy = top - s.lastTop;
          s.lastTop = top;
          // Reads before the one write below, so no layout thrash. The sticky
          // offset is NOT among them — see s.stickyTop.
          const threshold = root.getBoundingClientRect().top + s.stickyTop;
          const rowTop = el.getBoundingClientRect().top;
          const pinned = rowTop <= threshold + 0.5;
          // THE ARRIVAL FRAME. A scroll event carries a whole frame's travel, and
          // in the frame the row reaches its offset only the pixels AFTER it got
          // there are collapse; the rest carried the row down to the offset while
          // it was still in flow. Spending them all collapsed the row by a
          // flick's worth in one bite and, worse, opened a matching gap between
          // it and the row pinned beneath it (that one is still in flow, moving
          // with the list, and never catches up until the collapse ends). The
          // row moves 1:1 with the list while in flow, so the pre-pin share is
          // simply how far above the offset it sat at the last event.
          const collapse =
            pinned && !s.wasPinned && dy > 0 ? Math.max(0, dy - (s.lastRowTop - threshold)) : dy;
          s.wasPinned = pinned;
          s.lastRowTop = rowTop;
          // Not yet pinned (or scrolled to the very top): simply open. Pinned:
          // it follows the finger, a pixel per pixel, clamped to its box.
          const next = !pinned || top <= 0 ? s.h : clamp(snap(s.reveal - collapse), 0, s.h);
          if (next === s.reveal) return;
          s.reveal = next;
          root.style.setProperty(v.reveal, `${next}px`);
        };
        root.addEventListener('scroll', s.onScroll, { passive: true });

        if (typeof ResizeObserver !== 'undefined') {
          s.ro = new ResizeObserver(() => {
            // Before the height gate: crossing a breakpoint can change the
            // sticky offset while leaving the height alone.
            s.stickyTop = parseFloat(getComputedStyle(el).top) || 0;
            const h = snapUp(measured.getBoundingClientRect().height);
            if (h === s.h) return;
            // Keep the same openness through a height change.
            const open = s.h > 0 ? s.reveal / s.h : 1;
            s.h = h;
            s.reveal = clamp(snap(open * h), 0, h);
            publish(root);
          });
          s.ro.observe(measured);
        }
      };
      attach(0);
    },
    [detach]
  );

  useEffect(() => detach, [detach]);

  return setEl;
}
